/**
 * Dialog per cambiare l'intestatario (cliente per vendita, fornitore per
 * acquisto) DI QUESTO preventivo, senza duplicarlo (backend gia' pronto in
 * ordini/backend/services.py::cambia_intestatario_preventivo). Chiude la
 * causa che aveva costretto Walter a duplicare un preventivo solo per
 * cambiargli il cliente, ritrovandosi con uno scarto in bozza (v. anche
 * `elimina_preventivo_bozza`, usato da PreventivoDetail per smaltirlo).
 *
 * Il picker e' un Combobox con ricerca SERVER-SIDE su /api/v1/soggetti
 * (parametro `q`, limite basso, debounce, guardia anti-sorpasso), stesso
 * pattern di `DuplicaPreventivoDialog.tsx`: vietato scaricare tutti i
 * soggetti e filtrare in memoria.
 *
 * L'endpoint PATCH /{id} accetta `intestatario_id` solo su preventivo in
 * stato 'bozza' (regola PIU' RESTRITTIVA del resto del PATCH, che ammette
 * anche 'in_revisione') e solo un soggetto dello stesso tipo del preventivo
 * (cliente per vendita, fornitore per acquisto), rifiutando il resto con
 * 422: la UI propone quindi solo soggetti di quel tipo.
 *
 * Cambiando intestatario il backend azzera i dati del VECCHIO soggetto
 * (destinatari_extra_emails, rappresentante_id, storico invii in
 * `meta['invii']`): l'avviso qui sotto lo rende esplicito PRIMA della
 * conferma, perche' e' esattamente cio' che impedisce che un'offerta parta
 * verso il cliente sbagliato (non e' un dettaglio ovvio).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type ComboboxOption,
} from '@floraltech/ui';

interface SoggettoOpt {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preventivoId: string;
  tipo: 'vendita' | 'acquisto';
  /** Nome dell'intestatario attuale, solo per contesto in intestazione. */
  intestatarioAttuale: string | null;
  token: string | null;
  /** Chiamato dopo un cambio riuscito: il chiamante ricarica il dettaglio
   * (la PATCH risponde con uno schema piu' snello del GET dettaglio, senza
   * righe/nome intestatario risolto, quindi non basta a se stesso per
   * aggiornare la schermata). */
  onSuccess: () => void;
}

export function CambiaIntestatarioDialog({
  open,
  onOpenChange,
  preventivoId,
  tipo,
  intestatarioAttuale,
  token,
  onSuccess,
}: Props) {
  const tipoSoggetto = tipo === 'vendita' ? 'cliente' : 'fornitore';

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SoggettoOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selezionato, setSelezionato] = useState<SoggettoOpt | null>(null);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Reset dello stato interno solo all'apertura: evita di trascinare la
  // ricerca/selezione di un cambio precedente (stesso pattern di
  // DuplicaPreventivoDialog).
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelezionato(null);
      setSaving(false);
      setErrore(null);
    }
  }, [open]);

  // Identifica la ricerca corrente: incrementata prima di ogni fetch, per
  // scartare risposte tardive di query precedenti (sorpasso) senza toccare
  // risultati/caricamento di quella in corso.
  const ricercaGenerazioneRef = useRef(0);

  useEffect(() => {
    if (!open || selezionato) return;
    const timer = setTimeout(async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      ricercaGenerazioneRef.current += 1;
      const generazione = ricercaGenerazioneRef.current;
      setLoading(true);
      setErrore(null);
      try {
        const params = new URLSearchParams({ tipo: tipoSoggetto, q: query.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (ricercaGenerazioneRef.current !== generazione) return;
        if (res.ok) {
          const data = await res.json();
          if (ricercaGenerazioneRef.current !== generazione) return;
          setResults(Array.isArray(data) ? data : (data.items ?? []));
        } else {
          setResults([]);
          setErrore(`Ricerca soggetti fallita (${res.status})`);
        }
      } catch {
        if (ricercaGenerazioneRef.current === generazione) {
          setResults([]);
          setErrore('Errore di rete durante la ricerca: verifica la connessione e riprova.');
        }
      } finally {
        if (ricercaGenerazioneRef.current === generazione) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, selezionato, tipoSoggetto, token]);

  const label = (s: SoggettoOpt) =>
    s.ragione_sociale || [s.nome, s.cognome].filter(Boolean).join(' ') || '-';

  const handleConferma = async () => {
    if (!selezionato || saving) return;
    setSaving(true);
    setErrore(null);
    try {
      const res = await fetch(`/api/v1/preventivi/${preventivoId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ intestatario_id: selezionato.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        // detail e' stringa per gli errori di dominio (422 da ValueError nel
        // service, o 409 se lo stato e' cambiato nel frattempo); la
        // validazione automatica di FastAPI restituisce invece un ARRAY di
        // oggetti, mai da renderizzare cosi' (stesso caso di Duplica).
        setErrore(typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setSaving(false);
    }
  };

  const titolo = tipo === 'vendita' ? 'Cambia cliente' : 'Cambia fornitore';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {intestatarioAttuale && (
            <p className="text-sm text-muted-foreground">
              Intestatario attuale: <strong>{intestatarioAttuale}</strong>
            </p>
          )}
          <p className="text-sm text-destructive">
            Cambiando intestatario vengono azzerati su questo preventivo: i destinatari
            email aggiuntivi, il rappresentante assegnato e lo storico degli invii gia'
            effettuati. Serve a evitare che l&apos;offerta risulti ancora indirizzata al
            vecchio {tipoSoggetto}.
          </p>
          <div>
            <label className="text-xs text-muted-foreground">
              {tipo === 'vendita' ? 'Nuovo cliente' : 'Nuovo fornitore'}
            </label>
            {!selezionato ? (
              <>
                <Combobox
                  value={query}
                  onInputChange={setQuery}
                  onSelect={(opt) => {
                    const s = results.find((x) => x.id === opt.value);
                    if (s) setSelezionato(s);
                  }}
                  options={results.map<ComboboxOption>((s) => ({ value: s.id, label: label(s) }))}
                  loading={loading}
                  placeholder={tipo === 'vendita' ? "Digita nome del cliente..." : 'Digita nome del fornitore...'}
                  autoFocus
                />
                {!loading && !errore && query.trim() && results.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nessun risultato per &quot;{query.trim()}&quot;.
                  </p>
                )}
              </>
            ) : (
              <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                <span>{label(selezionato)}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelezionato(null)}>
                  Cambia
                </Button>
              </div>
            )}
          </div>
          {errore && <p className="text-sm text-destructive">{errore}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annulla
          </Button>
          <Button type="button" variant="destructive" onClick={() => void handleConferma()} disabled={saving || !selezionato}>
            {saving ? 'Salvataggio...' : 'Conferma cambio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
